import type { ReactElement } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Paperclip } from 'lucide-react';
import {
  createActionsColumn,
  createSerialColumn,
  DrillDownCount,
  DrillDownCountButton,
  DrillName,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import {
  RowActionButton,
  RowActionGroup,
  RowActionList,
  RowViewRender,
} from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber, pluralize } from '@/utils/number';
import type { InstituteListVariant, MonitorScreen } from '../constants/question.constants';
import type {
  Assignment,
  ClassSubject,
  InstituteSubject,
  MonitorDetailRow,
  MonitorRow,
  QuestionClass,
  QuestionProject,
  QuestionSubject,
  SubjectListType,
  UploadSubject,
} from '../types/question.types';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? '—' : value;

const count = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

function progressCell(assigned: number, collected: number, newCount: number) {
  const pct = assigned === 0 ? 0 : Math.round((collected / assigned) * 100);

  return (
    <span className="flex flex-col">
      <span className="tabular-nums">
        {formatNumber(collected)} / {formatNumber(assigned)} ({pct}%)
      </span>
      {newCount > 0 ? (
        <span className="text-xs text-emerald-700 dark:text-emerald-400">
          {formatNumber(newCount)} new
        </span>
      ) : null}
    </span>
  );
}

export interface MonitorDrillLinks {
  getGroupLink: (row: MonitorRow) => ReactElement | null;
  getNewLink: (row: MonitorRow) => ReactElement | null;
}

const serial = createSerialColumn<MonitorRow>();

function groupNameColumn(
  header: string,
  getLink: (row: MonitorRow) => ReactElement | null,
): ColumnDef<MonitorRow, unknown> {
  return {
    accessorKey: 'groupName',
    header,
    cell: ({ row }) => {
      const link = getLink(row.original);
      if (link === null) return dash(row.original.groupName);
      return (
        <DrillName
          value={row.original.groupName}
          label={`Open ${row.original.groupName ?? 'this row'}`}
          render={link}
        />
      );
    },
  };
}

function drillColumn(
  id: string,
  header: string,
  pick: (row: MonitorRow) => number,
  getLink: (row: MonitorRow) => ReactElement | null,
): ColumnDef<MonitorRow, unknown> {
  return {
    id,
    header,
    cell: ({ row }) => {
      const link = getLink(row.original);
      if (link === null) return count(pick(row.original));
      return (
        <DrillDownCount
          count={pick(row.original)}
          hint={`View ${header} under ${row.original.groupName ?? 'this row'}`}
          render={link}
        />
      );
    },
  };
}

function viewGroupColumn(
  getLink: (row: MonitorRow) => ReactElement | null,
): ColumnDef<MonitorRow, unknown> {
  return createActionsColumn<MonitorRow>((row) => {
    const link = getLink(row);
    if (link === null) return null;
    return <RowViewRender render={link} ariaLabel={`View ${row.groupName ?? 'this row'}`} />;
  });
}

function plainColumn(
  id: string,
  header: string,
  pick: (row: MonitorRow) => number,
): ColumnDef<MonitorRow, unknown> {
  return { id, header, cell: ({ row }) => count(pick(row.original)) };
}

function memberColumns(drill: MonitorDrillLinks): ColumnDef<MonitorRow, unknown>[] {
  const { getGroupLink, getNewLink } = drill;

  return [
    serial,
    {
      accessorKey: 'groupName',
      header: 'Zonal Name - HR ID (Cell)',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.groupName)}
          secondary={
            [row.original.username, row.original.userMobile]
              .filter((part) => part !== null && part !== '')
              .join(' · ') || undefined
          }
        />
      ),
    },
    {
      id: 'area',
      header: 'HR Division, District, Thana',
      cell: ({ row }) =>
        dash(
          [row.original.divisionName, row.original.districtName, row.original.thanaName]
            .filter((part) => part !== null && part !== '')
            .join(', '),
        ),
    },
    drillColumn(
      'instituteCount',
      'Total Institute Assigned',
      (row) => row.instituteCount,
      getGroupLink,
    ),
    drillColumn(
      'assignedCount',
      'Total Subject Assigned',
      (row) => row.assignedCount,
      getGroupLink,
    ),
    drillColumn(
      'collectedCount',
      'Total Subject Collected',
      (row) => row.collectedCount,
      getGroupLink,
    ),
    drillColumn('newCount', 'New Collected', (row) => row.newCount, getNewLink),
    viewGroupColumn(getGroupLink),
  ];
}

function byBoardColumns(
  groupColumnHeader: string,
  drill: MonitorDrillLinks,
): ColumnDef<MonitorRow, unknown>[] {
  const { getGroupLink } = drill;

  return [
    serial,
    groupNameColumn(groupColumnHeader, getGroupLink),
    drillColumn('memberCount', 'Total Zonal', (row) => row.memberCount, getGroupLink),
    drillColumn(
      'instituteCount',
      'Total Institute Assigned',
      (row) => row.instituteCount,
      getGroupLink,
    ),
    drillColumn(
      'assignedCount',
      'Total Subject Assigned',
      (row) => row.assignedCount,
      getGroupLink,
    ),
    drillColumn(
      'collectedCount',
      'Total Subject Collected',
      (row) => row.collectedCount,
      getGroupLink,
    ),
    drillColumn('newCount', 'New Collected', (row) => row.newCount, getGroupLink),
    viewGroupColumn(getGroupLink),
  ];
}

function byClassColumns(
  groupColumnHeader: string,
  drill: MonitorDrillLinks,
): ColumnDef<MonitorRow, unknown>[] {
  return [
    serial,
    groupNameColumn(groupColumnHeader, drill.getGroupLink),
    drillColumn(
      'instituteCount',
      'Total Institute Assigned',
      (row) => row.instituteCount,
      drill.getGroupLink,
    ),
    plainColumn('assignedCount', 'Total Subject Assigned', (row) => row.assignedCount),
    plainColumn('collectedCount', 'Total Subject Collected', (row) => row.collectedCount),
    drillColumn('newCount', 'New Collected', (row) => row.newCount, drill.getNewLink),
    viewGroupColumn(drill.getGroupLink),
  ];
}

export function getMonitorColumns(
  screen: MonitorScreen,
  groupColumnHeader: string,
  drill: MonitorDrillLinks,
): ColumnDef<MonitorRow, unknown>[] {
  if (screen === 'member') return memberColumns(drill);
  if (screen === 'byBoard') return byBoardColumns(groupColumnHeader, drill);
  return byClassColumns(groupColumnHeader, drill);
}

export function getMonitorDetailColumns(
  getPapersLink: (row: MonitorDetailRow) => ReactElement,
): ColumnDef<MonitorDetailRow, unknown>[] {
  return [
    createSerialColumn<MonitorDetailRow>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => <span className="font-medium">{dash(row.original.instituteName)}</span>,
    },
    {
      accessorKey: 'instituteEiin',
      header: 'EIIN',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">{dash(row.original.instituteEiin)}</span>
      ),
    },
    { accessorKey: 'boardName', header: 'Board', cell: ({ row }) => dash(row.original.boardName) },
    { accessorKey: 'userName', header: 'ZM Name', cell: ({ row }) => dash(row.original.userName) },
    {
      accessorKey: 'districtName',
      header: 'District',
      cell: ({ row }) => dash(row.original.districtName),
    },
    {
      id: 'progress',
      header: 'Collected',
      cell: ({ row }) =>
        progressCell(
          row.original.assignedCount,
          row.original.collectedCount,
          row.original.newCount,
        ),
    },
    createActionsColumn<MonitorDetailRow>((institute) => (
      <RowViewRender
        render={getPapersLink(institute)}
        label="Papers"
        ariaLabel={`Papers for ${institute.instituteName ?? 'this institute'}`}
      />
    )),
  ];
}

export function getMonitorClassColumns(
  getInstitutesLink: (row: MonitorRow, isNew: boolean) => ReactElement,
): ColumnDef<MonitorRow, unknown>[] {
  return [
    createSerialColumn<MonitorRow>(),
    {
      accessorKey: 'groupName',
      header: 'Class',
      cell: ({ row }) => <span className="font-medium">{dash(row.original.groupName)}</span>,
    },
    {
      accessorKey: 'instituteCount',
      header: 'Inst. Qty',
      cell: ({ row }) => (
        <DrillDownCount
          count={row.original.instituteCount}
          hint={`View institutes in ${row.original.groupName ?? 'this class'}`}
          render={getInstitutesLink(row.original, false)}
        />
      ),
    },
    {
      accessorKey: 'assignedCount',
      header: 'Assigned Subject',
      cell: ({ row }) => count(row.original.assignedCount),
    },
    {
      accessorKey: 'collectedCount',
      header: 'All Collection',
      cell: ({ row }) => count(row.original.collectedCount),
    },
    {
      id: 'newCount',
      header: 'New Collection',
      cell: ({ row }) => (
        <DrillDownCount
          count={row.original.newCount}
          hint={`View new collection in ${row.original.groupName ?? 'this class'}`}
          render={getInstitutesLink(row.original, true)}
        />
      ),
    },
  ];
}

export function getMonitorDistrictColumns(
  getInstitutesLink: (row: MonitorRow) => ReactElement,
): ColumnDef<MonitorRow, unknown>[] {
  return [
    createSerialColumn<MonitorRow>(),
    {
      accessorKey: 'groupName',
      header: 'District',
      cell: ({ row }) => <span className="font-medium">{dash(row.original.groupName)}</span>,
    },
    drillColumn('instituteCount', 'Inst. Qty', (row) => row.instituteCount, getInstitutesLink),
    drillColumn('assignedCount', 'Assigned Subject', (row) => row.assignedCount, getInstitutesLink),
    drillColumn('collectedCount', 'Collection', (row) => row.collectedCount, getInstitutesLink),
    drillColumn('newCount', 'New Collection', (row) => row.newCount, getInstitutesLink),
  ];
}

export function getMonitorInstituteColumns(
  variant: InstituteListVariant,
  getSubjectsLink: (
    row: MonitorDetailRow,
    subjectType: SubjectListType,
    isNew?: boolean,
  ) => ReactElement,
): ColumnDef<MonitorDetailRow, unknown>[] {
  const instituteColumn: ColumnDef<MonitorDetailRow, unknown> = {
    accessorKey: 'instituteName',
    header: 'Institution Name (Code)',
    cell: ({ row }) => (
      <StackedCellText
        primary={dash(row.original.instituteName)}
        secondary={
          row.original.instituteCode === null ? undefined : String(row.original.instituteCode)
        }
      />
    ),
  };

  const assignedColumn: ColumnDef<MonitorDetailRow, unknown> = {
    id: 'assignedCount',
    header: 'Total Subject Assigned',
    cell: ({ row }) => (
      <DrillDownCount
        count={row.original.assignedCount}
        hint={`View subjects assigned to ${row.original.instituteName ?? 'this institute'}`}
        render={getSubjectsLink(row.original, 'A')}
      />
    ),
  };

  const assignedAtColumn: ColumnDef<MonitorDetailRow, unknown> = {
    accessorKey: 'assignedAt',
    header: 'Assign Datetime',
    cell: ({ row }) => formatDisplayDate(row.original.assignedAt),
  };

  const collectedColumn = (header: string): ColumnDef<MonitorDetailRow, unknown> => ({
    id: 'collectedCount',
    header,
    cell: ({ row }) => (
      <DrillDownCount
        count={row.original.collectedCount}
        hint={`View subjects collected from ${row.original.instituteName ?? 'this institute'}`}
        render={getSubjectsLink(row.original, 'C', false)}
      />
    ),
  });

  if (variant === 'byUser') {
    return [
      createSerialColumn<MonitorDetailRow>(),
      instituteColumn,
      {
        id: 'area',
        header: 'Division, District, Thana',
        cell: ({ row }) =>
          dash(
            [row.original.divisionName, row.original.districtName, row.original.thanaName]
              .filter((part) => part !== null && part !== '')
              .join(', '),
          ),
      },
      assignedColumn,
      collectedColumn('Total Subject Collected'),
      assignedAtColumn,
    ];
  }

  const areaColumns: ColumnDef<MonitorDetailRow, unknown>[] = [
    createSerialColumn<MonitorDetailRow>(),
    instituteColumn,
    { accessorKey: 'thanaName', header: 'Thana', cell: ({ row }) => dash(row.original.thanaName) },
    {
      accessorKey: 'userName',
      header: 'User Name',
      cell: ({ row }) => dash(row.original.userName),
    },
    {
      accessorKey: 'userMobile',
      header: 'Contact No',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">{dash(row.original.userMobile)}</span>
      ),
    },
    assignedColumn,
  ];

  if (variant === 'byArea') {
    return [...areaColumns, collectedColumn('Total Subject Collected'), assignedAtColumn];
  }

  return [
    ...areaColumns,
    collectedColumn('All Collection'),
    {
      id: 'newCount',
      header: 'New Collection',
      cell: ({ row }) => (
        <DrillDownCount
          count={row.original.newCount}
          hint={`View new subjects collected from ${row.original.instituteName ?? 'this institute'}`}
          render={getSubjectsLink(row.original, 'C', true)}
        />
      ),
    },
    assignedAtColumn,
  ];
}

export interface SubjectActions {
  onDownload: (subject: InstituteSubject) => void;
  onReset: (subject: InstituteSubject) => void;
  canDownload: boolean;
  canReset: boolean;
}

export function getMonitorSubjectColumns(
  actions: SubjectActions,
  showDownloadCount: boolean,
): ColumnDef<InstituteSubject, unknown>[] {
  const columns: ColumnDef<InstituteSubject, unknown>[] = [
    createSerialColumn<InstituteSubject>(),
    {
      accessorKey: 'subjectName',
      header: 'Subject Name (Bangla)',
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.subjectName)}
          secondary={row.original.subjectNameLocal ?? undefined}
        />
      ),
    },
    {
      accessorKey: 'targetDate',
      header: 'Target Collection Date',
      cell: ({ row }) => formatDisplayDate(row.original.targetDate),
    },
    {
      id: 'isCollected',
      header: 'Is Collected',
      cell: ({ row }) =>
        row.original.isCollected ? (
          <span className="text-emerald-700 dark:text-emerald-400">Yes</span>
        ) : (
          <span className="text-muted-foreground">No</span>
        ),
    },
    {
      accessorKey: 'collectedAt',
      header: 'Collect Time',
      cell: ({ row }) => formatDisplayDate(row.original.collectedAt),
    },
  ];

  if (showDownloadCount) {
    columns.push({
      accessorKey: 'downloadCount',
      header: 'Download Count',
      cell: ({ row }) => count(row.original.downloadCount),
    });
  }

  columns.push(
    createActionsColumn<InstituteSubject>((subject) => {
      if (!subject.isCollected) return <EmptyValue />;

      const buttons = [
        actions.canDownload && subject.hasFile ? (
          <RowActionButton
            key="download"
            label="Download"
            ariaLabel={`Download ${subject.subjectName ?? 'this paper'}`}
            onClick={() => actions.onDownload(subject)}
          />
        ) : null,
        actions.canReset ? (
          <RowActionButton
            key="reset"
            label="Reset"
            ariaLabel={`Reset the attachment for ${subject.subjectName ?? 'this subject'}`}
            onClick={() => actions.onReset(subject)}
          />
        ) : null,
      ].filter(Boolean);

      if (buttons.length === 0) return <EmptyValue />;

      return <span className="flex items-center justify-end gap-1">{buttons}</span>;
    }),
  );

  return columns;
}

export interface ProjectRowActions {
  onOpenInstitutes: (project: QuestionProject) => void;
  onEdit: (project: QuestionProject) => void;
  onDelete: (project: QuestionProject) => void;
}

export function getProjectColumns(
  actions: ProjectRowActions,
  searchText?: string,
): ColumnDef<QuestionProject, unknown>[] {
  return [
    createSerialColumn<QuestionProject>(),
    {
      accessorKey: 'name',
      header: 'Collection Project Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    { accessorKey: 'className', header: 'Class', cell: ({ row }) => dash(row.original.className) },
    {
      accessorKey: 'instituteCount',
      header: 'Assigned Institute',
      cell: ({ row }) => count(row.original.instituteCount),
    },
    {
      accessorKey: 'pendingCount',
      header: 'Pending Institute',
      cell: ({ row }) => (
        <span
          className={row.original.pendingCount > 0 ? 'tabular-nums text-amber-700' : 'tabular-nums'}
        >
          {formatNumber(row.original.pendingCount)}
        </span>
      ),
    },
    {
      accessorKey: 'declareDate',
      header: 'Declare Date',
      cell: ({ row }) => formatDisplayDate(row.original.declareDate),
    },
    {
      accessorKey: 'deadlineDate',
      header: 'Deadline Date',
      cell: ({ row }) => formatDisplayDate(row.original.deadlineDate),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    createActionsColumn<QuestionProject>((project) => (
      <RowActionList
        moreActionsAriaLabel={`More actions for ${project.name}`}
        actions={[
          {
            key: 'institutes',
            label: 'Institutes',
            ariaLabel: `Institutes on ${project.name}`,
            onSelect: () => actions.onOpenInstitutes(project),
          },
          {
            key: 'edit',
            label: 'Edit',
            ariaLabel: `Edit ${project.name}`,
            onSelect: () => actions.onEdit(project),
          },
          {
            key: 'delete',
            label: 'Delete',
            ariaLabel: `Delete ${project.name}`,
            tone: 'destructive',
            onSelect: () => actions.onDelete(project),
          },
        ]}
      />
    )),
  ];
}

export interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete: (row: TRow) => void;
}

export interface SubjectRowActions extends RowActions<QuestionSubject> {
  onPutOnClass: ((subject: QuestionSubject) => void) | undefined;
}

export function getQuestionClassColumns(
  onOpen: (row: QuestionClass) => void,
  actions: RowActions<QuestionClass>,
): ColumnDef<QuestionClass, unknown>[] {
  return [
    createSerialColumn<QuestionClass>(),
    {
      accessorKey: 'name',
      header: 'Class Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'nameLocal',
      header: 'Class Name (Bangla)',
      cell: ({ row }) => dash(row.original.nameLocal),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => dash(row.original.code),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.description)}</WrappingCellText>,
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From',
      cell: ({ row }) => formatDisplayDate(row.original.activeFrom),
    },
    {
      accessorKey: 'activeTo',
      header: 'Active To',
      cell: ({ row }) => formatDisplayDate(row.original.activeTo),
    },
    {
      accessorKey: 'subjectCount',
      header: 'Subjects',
      cell: ({ row }) => {
        const { subjectCount, name } = row.original;
        return (
          <DrillDownCountButton
            count={subjectCount}
            hint={`View ${formatNumber(subjectCount)} ${pluralize(subjectCount, 'subject')} of ${name}`}
            onOpen={() => onOpen(row.original)}
          />
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    createActionsColumn<QuestionClass>((questionClass) => {
      const carriesSubjects = questionClass.subjectCount > 0;
      return (
        <RowActionGroup>
          <RowActionButton
            label="Edit"
            ariaLabel={`Edit ${questionClass.name}`}
            onClick={() => actions.onEdit?.(questionClass)}
          />
          <RowActionButton
            label="Delete"
            ariaLabel={
              carriesSubjects
                ? `${questionClass.name} carries ${formatNumber(questionClass.subjectCount)} ${pluralize(questionClass.subjectCount, 'subject')}; take those off first`
                : `Delete ${questionClass.name}`
            }
            onClick={() => actions.onDelete?.(questionClass)}
            isDisabled={carriesSubjects}
            tone="destructive"
          />
        </RowActionGroup>
      );
    }),
  ];
}

export function getQuestionSubjectColumns(
  searchText: string | undefined,
  actions: SubjectRowActions,
): ColumnDef<QuestionSubject, unknown>[] {
  const { onPutOnClass } = actions;
  return [
    createSerialColumn<QuestionSubject>(),
    {
      accessorKey: 'name',
      header: 'Subject',
      cell: ({ row }) => (
        <StackedCellText
          primary={<HighlightText text={row.original.name} highlightedText={searchText} />}
          primaryClassName="font-medium"
          secondary={row.original.nameLocal}
        />
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => <span className="text-xs tabular-nums">{dash(row.original.code)}</span>,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    createActionsColumn<QuestionSubject>((subject) => (
      <RowActionList
        moreActionsAriaLabel={`More actions for ${subject.name}`}
        actions={[
          {
            key: 'putOnClass',
            label: 'Put on class',
            ariaLabel: `Put ${subject.name} on the picked class`,
            isDisabled: onPutOnClass === undefined,
            onSelect: () => onPutOnClass?.(subject),
          },
          {
            key: 'edit',
            label: 'Edit',
            ariaLabel: `Edit ${subject.name}`,
            onSelect: () => actions.onEdit(subject),
          },
          {
            key: 'delete',
            label: 'Delete',
            ariaLabel: `Delete ${subject.name}`,
            tone: 'destructive',
            onSelect: () => actions.onDelete(subject),
          },
        ]}
      />
    )),
  ];
}

export function getClassSubjectColumns(
  onRemove: (row: ClassSubject) => void,
): ColumnDef<ClassSubject, unknown>[] {
  return [
    createSerialColumn<ClassSubject>(),
    {
      accessorKey: 'subjectName',
      header: 'Subject',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.subjectName}
          primaryClassName="font-medium"
          secondary={row.original.subjectLocal}
        />
      ),
    },
    {
      accessorKey: 'subjectCode',
      header: 'Code',
      cell: ({ row }) => (
        <span className="text-xs tabular-nums">{dash(row.original.subjectCode)}</span>
      ),
    },
    createActionsColumn<ClassSubject>((classSubject) => (
      <RowActionButton
        label="Take off"
        ariaLabel={`Take ${classSubject.subjectName ?? 'this subject'} off the class`}
        tone="destructive"
        onClick={() => onRemove(classSubject)}
      />
    )),
  ];
}

export function getMyAssignmentColumns(
  onOpen: (row: Assignment) => void,
  deadlineDate?: string | null,
): ColumnDef<Assignment, unknown>[] {
  return [
    createSerialColumn<Assignment>(),
    {
      accessorKey: 'instituteName',
      header: 'Assigned Institute Name (Code)',
      cell: ({ row }) => (
        <WrappingCellText>
          <button
            type="button"
            className="text-left font-medium text-primary hover:underline"
            onClick={() => onOpen(row.original)}
          >
            {dash(row.original.instituteName)}
          </button>
          {row.original.instituteCode === null ? null : (
            <span className="block text-xs tabular-nums text-muted-foreground">
              {row.original.instituteCode}
            </span>
          )}
        </WrappingCellText>
      ),
    },
    {
      id: 'where',
      header: 'Division, District, Thana',
      cell: ({ row }) =>
        dash(
          [row.original.divisionName, row.original.districtName, row.original.thanaName]
            .filter(Boolean)
            .join(', '),
        ),
    },
    {
      accessorKey: 'assignedCount',
      header: 'No. of Subject Assigned',
      cell: ({ row }) => count(row.original.assignedCount),
    },
    {
      id: 'pending',
      header: 'Pending Upload',
      cell: ({ row }) => {
        const pending = Math.max(row.original.assignedCount - row.original.collectedCount, 0);
        return (
          <span className={pending > 0 ? 'tabular-nums text-amber-700' : 'tabular-nums'}>
            {formatNumber(pending)}
          </span>
        );
      },
    },
    {
      id: 'deadline',
      header: 'Project Deadline Date',
      cell: () => formatDisplayDate(deadlineDate ?? null),
    },
    createActionsColumn<Assignment>((assignment) => (
      <RowActionButton
        label="Select"
        ariaLabel={`Subjects for ${assignment.instituteName ?? 'this institute'}`}
        onClick={() => onOpen(assignment)}
      />
    )),
  ];
}

const uploadSubjectNameColumn: ColumnDef<UploadSubject, unknown> = {
  accessorKey: 'subjectName',
  header: 'Assigned Subject Name',
  cell: ({ row }) => (
    <StackedCellText
      primary={dash(row.original.subjectName)}
      primaryClassName="font-medium"
      secondary={row.original.subjectLocal}
    />
  ),
};

const uploadTargetColumn: ColumnDef<UploadSubject, unknown> = {
  id: 'target',
  header: 'Target Collection Date',
  cell: ({ row }) => formatDisplayDate(row.original.targetDate),
};

const uploadFilesColumn: ColumnDef<UploadSubject, unknown> = {
  id: 'files',
  header: 'No. of File Attach.',
  cell: ({ row }) =>
    row.original.uploadCount === 0 ? (
      <span className="text-muted-foreground">Nothing in yet</span>
    ) : (
      <span className="inline-flex items-center gap-1">
        <Paperclip className="size-3.5" />
        {row.original.uploadCount}
      </span>
    ),
};

export function getInstitutePaperColumns(
  onOpenPapers: (row: UploadSubject) => void,
): ColumnDef<UploadSubject, unknown>[] {
  return [
    createSerialColumn<UploadSubject>(),
    uploadSubjectNameColumn,
    uploadTargetColumn,
    uploadFilesColumn,
    createActionsColumn<UploadSubject>((subject) => (
      <RowActionButton
        label="Papers"
        ariaLabel={`Papers for ${subject.subjectName ?? 'this subject'}`}
        onClick={() => onOpenPapers(subject)}
        isDisabled={subject.uploadCount === 0}
        disabledHint="No paper is in yet"
      />
    )),
  ];
}

export function getUploadSubjectColumns(
  onManage: (row: UploadSubject) => void,
): ColumnDef<UploadSubject, unknown>[] {
  return [
    createSerialColumn<UploadSubject>(),
    uploadSubjectNameColumn,
    uploadTargetColumn,
    {
      id: 'alreadyUploaded',
      header: 'Already Uploaded',
      cell: ({ row }) =>
        row.original.uploadCount > 0 ? (
          <span className="text-emerald-700 dark:text-emerald-400">Yes</span>
        ) : (
          <span className="text-muted-foreground">No</span>
        ),
    },
    uploadFilesColumn,
    createActionsColumn<UploadSubject>((subject) => {
      const hasUploads = subject.uploadCount > 0;
      const label = hasUploads ? 'Attach More Files' : 'Attach';
      return (
        <RowActionButton
          label={label}
          ariaLabel={`${label} papers for ${subject.subjectName ?? 'this subject'}`}
          onClick={() => onManage(subject)}
        />
      );
    }),
  ];
}
